import { useEffect, useState } from 'react';

/**
 * «Последний запрос побеждает» для загрузчиков, которые перезапрашивают данные при смене
 * параметров (период, фильтры) и вдобавок опрашивают сервер по таймеру.
 *
 * Раньше вместо этого стоял `isFetching`: пока запрос в полёте, новый просто не запускался.
 * Против наложения опросов по таймеру этого хватало, но так же отбрасывался и запрос с новыми
 * параметрами. Календарь отдавал период дважды — один день на первом клике, весь диапазон на
 * втором, — и запрос за диапазон терялся: графики оставались на дне первого клика, пока вкладку
 * не перемонтируют. Без охранника гонка обратная — медленный ответ на старые параметры,
 * пришедший последним, затирает свежий.
 *
 * Правила:
 * - обычный запрос (смена параметров, кнопка «Обновить») стартует всегда и делает устаревшими
 *   все предыдущие;
 * - фоновый (`background: true`, таймер) пропускается, пока не закончился последний запрос:
 *   медленная база не должна копить очередь опросов — ради этого и стоял `isFetching`;
 * - данные, ошибку и снятие спиннера применяет только текущий запрос (`isCurrent()`).
 */
export const createLatestRequestGate = () => {
  let latestId = 0;
  let latestPending = false;

  return {
    /** Возвращает `null`, если фоновый запрос пропущен. */
    begin({ background = false } = {}) {
      if (background && latestPending) {
        return null;
      }

      latestId += 1;
      latestPending = true;
      const id = latestId;

      return {
        isCurrent: () => id === latestId,
        end: () => {
          if (id === latestId) {
            latestPending = false;
          }
        }
      };
    },
    /** Всё, что в полёте, становится устаревшим (размонтирование). */
    reset() {
      latestId += 1;
      latestPending = false;
    }
  };
};

/**
 * Гейт на время жизни компонента. Ленивый `useState`, а не `useRef`: экземпляр стабилен так же,
 * но читается в рендере без нарушения `react-hooks/refs`, и его можно класть в зависимости.
 */
const useLatestRequest = () => {
  const [gate] = useState(createLatestRequestGate);

  useEffect(() => () => gate.reset(), [gate]);

  return gate;
};

export default useLatestRequest;
